import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ConversationDisplay from '../../ConversationDisplay';
import MessageInput from './MessageInput';

const ConversationManager = ({
  conversation: controlledConversation = null,
  onConversationChange,
  conversationMode,
  conversationStarted,
  loading,
  dataChannel,
  onMessageReceived,
  onConversationUpdate,
  onMessageCountUpdate,
  tools = []
}) => {
  const [localConversation, setLocalConversation] = useState([]);
  const isControlled = Array.isArray(controlledConversation);
  const conversation = isControlled ? controlledConversation : localConversation;

  const updateConversation = useCallback((nextConversationOrUpdater) => {
    const nextConversation = typeof nextConversationOrUpdater === 'function'
      ? nextConversationOrUpdater(conversation)
      : nextConversationOrUpdater;

    if (!isControlled) {
      setLocalConversation(nextConversation);
    }

    if (onConversationChange) {
      onConversationChange(nextConversation);
    }

    return nextConversation;
  }, [conversation, isControlled, onConversationChange]);

  // Handle WebRTC messages
  const handleWebRTCMessage = useCallback((event) => {
    try {
      const responseData = JSON.parse(event.data);
      const isTranscriptDone = responseData.type === "response.audio_transcript.done" ||
        responseData.type === "response.output_audio_transcript.done";
      const isTextDelta = responseData.type === "response.text.delta" ||
        responseData.type === "response.output_text.delta";
      const isTextDone = responseData.type === "response.text.done" ||
        responseData.type === "response.output_text.done";

      // Handle regular conversation messages
      if (isTranscriptDone && responseData.transcript) {
        updateConversation((prev) => [
          ...prev,
          { role: 'assistant', content: responseData.transcript, timestamp: new Date().toISOString(), channel: 'voice' }
        ]);
        if (onMessageCountUpdate) {
          onMessageCountUpdate(prev => prev + 1);
        }
      }

      if (isTextDelta && responseData.text) {
        updateConversation((prev) => {
          if (prev.length > 0 && prev[prev.length - 1].role === 'assistant' && prev[prev.length - 1].incomplete) {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: `${updated[updated.length - 1].content}${responseData.text}`,
            };
            return updated;
          } else {
            return [
              ...prev,
              { role: 'assistant', content: responseData.text, timestamp: new Date().toISOString(), incomplete: true }
            ];
          }
        });
      }

      if (isTextDone && responseData.text) {
        updateConversation((prev) => {
          if (prev.length > 0 && prev[prev.length - 1].role === 'assistant' && prev[prev.length - 1].incomplete) {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: `${updated[updated.length - 1].content}${responseData.text}`,
              incomplete: false,
            };
            return updated;
          } else {
            return [
              ...prev,
              { role: 'assistant', content: responseData.text, timestamp: new Date().toISOString() }
            ];
          }
        });
        if (onMessageCountUpdate) {
          onMessageCountUpdate(prev => prev + 1);
        }
      }

      // Forward the message to parent for additional processing (like function calls)
      if (onMessageReceived) {
        onMessageReceived(event);
      }
    } catch (error) {
      console.error("Error processing AI response:", error);
    }
  }, [onMessageCountUpdate, onMessageReceived, updateConversation]);

  // Send conversation via WebRTC data channel
  const sendConversation = useCallback((conversationToSend, retryCount = 0) => {
    console.log(`🔍 Data channel state: ${dataChannel ? dataChannel.readyState : 'null'}`);
    if (!dataChannel || dataChannel.readyState !== "open") {
      if (retryCount < 5) {
        console.warn(`⚠️ Data channel is not open yet. State: ${dataChannel ? dataChannel.readyState : 'null'}. Retrying... (${retryCount + 1}/5)`);
        setTimeout(() => sendConversation(conversationToSend, retryCount + 1), 2000);
        return;
      } else {
        console.error("❌ Data channel failed to open after 5 retries. Aborting message send.");
        return;
      }
    }

    // Ensure conversation has a system prompt
    if (!conversationToSend || conversationToSend.length === 0 || conversationToSend[0].role !== "system") {
      console.error("❌ No system prompt in conversation");
      return;
    }

    let instructionsString = "";
    let systemContent = "";
    if (conversationToSend.length > 0 && conversationToSend[0].role === "system") {
      systemContent = conversationToSend[0].content;
      if (conversationMode === "text") {
        instructionsString += `[system] ${systemContent}\n`;
      } else {
        if (conversationToSend.length === 1 || (conversationToSend.length === 2 && conversationToSend[1].role === "user")) {
          instructionsString += `[system] ${systemContent}\n`;
        } else {
          const importantIndex = systemContent.indexOf("IMPORTANT:");
          if (importantIndex !== -1) {
            instructionsString += `[system] ${systemContent.substring(importantIndex).trim()}\n`;
          } else {
            instructionsString += `[system] ${systemContent}\n`;
          }
        }
      }
    }

    if (conversationToSend.length > 4) {
      const oldMessages = conversationToSend.slice(1, conversationToSend.length - 3);
      let summary = oldMessages.map(m => `${m.role}: ${m.content}`).join(" / ");
      if (summary.length > 200) summary = summary.substring(0, 200) + "...";
      instructionsString += `[summary] ${summary}\n`;
    }

    const recentMessages = conversationToSend.slice(Math.max(1, conversationToSend.length - 3));
    const filteredMessages = recentMessages.filter(msg => {
      if (msg.role === "assistant") {
        return msg.content.trim() !== systemContent.trim();
      }
      return true;
    });

    instructionsString += filteredMessages.map(msg => `[${msg.role}] ${msg.content}`).join("\n");
    let modalities = [];

    if (conversationMode === "text") {
      modalities = ['text'];
    } else if (conversationMode === "voice") {
      modalities = ['audio'];
    } else if (conversationMode === "voiceText") {
      modalities = ['audio'];
    }

    const payload = {
      type: "response.create",
      response: {
        output_modalities: modalities,
        instructions: instructionsString,
        tools,
      },
    };

    try {
      dataChannel.send(JSON.stringify(payload));
      console.log("Sent conversation event:", payload);
    } catch (error) {
      console.error("Error sending conversation via Data Channel:", error);
    }
  }, [conversationMode, dataChannel, tools]);

  // Handle sending user messages
  const handleSendMessage = async (userMessage) => {
    if (!userMessage.trim()) return;
    
    const newUserMessage = { role: "user", content: userMessage, timestamp: new Date().toISOString() };
    const updatedConversation = [...conversation, newUserMessage];
    
    if (onMessageCountUpdate) {
      onMessageCountUpdate(prev => prev + 1);
    }
    
    updateConversation(updatedConversation);
    
    try {
      // Only send if we have a system prompt
      if (updatedConversation.length > 0 && updatedConversation[0].role === "system") {
        sendConversation(updatedConversation);
      } else {
        console.warn("Cannot send message without system prompt");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      throw error; // Re-throw to let MessageInput handle it
    }
  };

  // Initialize conversation with system prompt
  const initializeConversation = useCallback((systemPrompt) => {
    const initialConversation = [{ role: "system", content: systemPrompt, timestamp: new Date().toISOString() }];
    updateConversation(initialConversation);
    return initialConversation;
  }, [updateConversation]);

  // Clear conversation
  const clearConversation = useCallback(() => {
    updateConversation([]);
  }, [updateConversation]);

  const conversationMethods = useMemo(() => ({
    handleWebRTCMessage,
    initializeConversation,
    clearConversation,
    sendConversation: (conv) => sendConversation(conv || conversation)
  }), [clearConversation, conversation, handleWebRTCMessage, initializeConversation, sendConversation]);

  // Expose methods to parent component
  useEffect(() => {
    if (onConversationUpdate) {
      onConversationUpdate(conversation, conversationMethods);
    }
  }, [conversation, conversationMethods, onConversationUpdate]);

  return (
    <div className="conversation-manager">
      {conversationStarted && (conversationMode === "text" || conversationMode === "voiceText") && (
        <ConversationDisplay conversation={conversation} />
      )}
      
      <MessageInput
        conversationMode={conversationMode}
        conversationStarted={conversationStarted}
        loading={loading}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};

ConversationManager.propTypes = {
  conversation: PropTypes.array,
  onConversationChange: PropTypes.func,
  conversationMode: PropTypes.oneOf(['text', 'voice', 'voiceText']).isRequired,
  conversationStarted: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  dataChannel: PropTypes.object,
  onMessageReceived: PropTypes.func,
  onConversationUpdate: PropTypes.func,
  onMessageCountUpdate: PropTypes.func,
  tools: PropTypes.array
};

export default ConversationManager;
